/**
 * Offline-First API Service
 * Wraps all API calls with offline fallback
 * - Returns cached data when server unreachable
 * - Queues write operations for sync
 * - NO "failed to fetch" errors - graceful degradation
 */

import axios from 'axios';
import offlineDB, { STORES, logAuditAction, addToSyncQueue } from './offlineDB';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Connection state
let isOnline = navigator.onLine;
let isServerReachable = true;
let lastServerCheck = 0;
const SERVER_CHECK_INTERVAL = 30000; // 30 seconds

// Event listeners for connection state
const connectionListeners = [];

/**
 * Create the base axios instance
 */
const createAxiosInstance = (token = null) => {
  const instance = axios.create({
    baseURL: API_URL,
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    }
  });

  return instance;
};

/**
 * Check if server is reachable
 */
export const checkServerConnection = async () => {
  const now = Date.now();
  if (now - lastServerCheck < SERVER_CHECK_INTERVAL) {
    return isServerReachable;
  }

  lastServerCheck = now;

  try {
    const response = await axios.get(`${API_URL}/health`, { timeout: 5000 });
    isServerReachable = response.status === 200;
  } catch (error) {
    isServerReachable = false;
  }

  notifyConnectionChange();
  return isServerReachable;
};

/**
 * Get connection state
 */
export const getConnectionState = () => ({
  isOnline,
  isServerReachable,
  isFullyOnline: isOnline && isServerReachable
});

/**
 * Subscribe to connection changes
 */
export const onConnectionChange = (callback) => {
  connectionListeners.push(callback);
  return () => {
    const index = connectionListeners.indexOf(callback);
    if (index > -1) connectionListeners.splice(index, 1);
  };
};

/**
 * Notify listeners of connection change
 */
const notifyConnectionChange = () => {
  const state = getConnectionState();
  connectionListeners.forEach(cb => cb(state));
};

// Listen for browser online/offline events
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    isOnline = true;
    checkServerConnection();
    notifyConnectionChange();
  });

  window.addEventListener('offline', () => {
    isOnline = false;
    isServerReachable = false;
    notifyConnectionChange();
  });
}

// ==================== STORE MAPPINGS ====================

// Map API endpoints to IndexedDB stores
const ENDPOINT_STORE_MAP = {
  // Menu
  '/restaurants/*/menu': STORES.MENU_ITEMS,
  '/menu-items': STORES.MENU_ITEMS,
  
  // Orders
  '/orders': STORES.ORDERS,
  '/restaurants/*/orders': STORES.ORDERS,
  
  // Tables
  '/restaurants/*/tables': STORES.TABLES,
  '/table-sessions': STORES.TABLE_SESSIONS,
  '/table-bookings': STORES.BOOKINGS,
  
  // Stock
  '/restaurants/*/stock': STORES.STOCK,
  '/stock': STORES.STOCK,
  
  // Staff
  '/restaurants/*/staff': STORES.STAFF,
  '/restaurants/*/waiters': STORES.WAITERS,
  
  // Restaurant
  '/restaurants/*': STORES.RESTAURANT,
};

/**
 * Find matching store for an endpoint
 */
const findStoreForEndpoint = (endpoint) => {
  for (const [pattern, store] of Object.entries(ENDPOINT_STORE_MAP)) {
    const regex = new RegExp('^' + pattern.replace(/\*/g, '[^/]+') + '(/.*)?$');
    if (regex.test(endpoint)) {
      return store;
    }
  }
  return null;
};

/**
 * Extract restaurant ID from endpoint
 */
const extractRestaurantId = (endpoint) => {
  const match = endpoint.match(/\/restaurants\/([^/]+)/);
  return match ? match[1] : null;
};

// ==================== OFFLINE-FIRST API ====================

/**
 * Create offline-first API wrapper
 */
export const createOfflineAPI = (getToken) => {
  const api = {
    /**
     * GET request with offline fallback
     */
    async get(endpoint, config = {}) {
      const token = getToken?.();
      const axiosInstance = createAxiosInstance(token);
      
      try {
        // Try online request first
        if (isOnline && isServerReachable) {
          const response = await axiosInstance.get(endpoint, config);
          
          // Cache the response
          await cacheResponse(endpoint, response.data);
          
          return response;
        }
        throw new Error('Offline');
      } catch (error) {
        // Fallback to cached data
        const cachedData = await getCachedData(endpoint);
        
        if (cachedData !== null) {
          console.log(`OfflineAPI: Serving cached data for ${endpoint}`);
          return { data: cachedData, status: 200, offline: true };
        }
        
        // If no cached data, check if it's a network error
        if (error.message === 'Offline' || error.code === 'ECONNABORTED' || !error.response) {
          isServerReachable = false;
          notifyConnectionChange();
          
          // Return empty but valid response
          return { data: [], status: 200, offline: true, empty: true };
        }
        
        throw error;
      }
    },

    /**
     * POST request with offline queue
     */
    async post(endpoint, data, config = {}) {
      const token = getToken?.();
      const axiosInstance = createAxiosInstance(token);
      
      try {
        if (isOnline && isServerReachable) {
          const response = await axiosInstance.post(endpoint, data, config);
          
          // Cache the created item
          if (response.data && response.data.id) {
            await cacheCreatedItem(endpoint, response.data);
          }
          
          return response;
        }
        throw new Error('Offline');
      } catch (error) {
        if (error.message === 'Offline' || error.code === 'ECONNABORTED' || !error.response) {
          isServerReachable = false;
          notifyConnectionChange();
          
          // Create locally and queue for sync
          return await handleOfflineCreate(endpoint, data, getToken);
        }
        throw error;
      }
    },

    /**
     * PUT request with offline queue
     */
    async put(endpoint, data, config = {}) {
      const token = getToken?.();
      const axiosInstance = createAxiosInstance(token);
      
      try {
        if (isOnline && isServerReachable) {
          const response = await axiosInstance.put(endpoint, data, config);
          
          // Update cache
          await updateCachedItem(endpoint, data);
          
          return response;
        }
        throw new Error('Offline');
      } catch (error) {
        if (error.message === 'Offline' || error.code === 'ECONNABORTED' || !error.response) {
          isServerReachable = false;
          notifyConnectionChange();
          
          return await handleOfflineUpdate(endpoint, data, getToken);
        }
        throw error;
      }
    },

    /**
     * PATCH request with offline queue
     */
    async patch(endpoint, data, config = {}) {
      return api.put(endpoint, data, config);
    },

    /**
     * DELETE request with offline queue
     */
    async delete(endpoint, config = {}) {
      const token = getToken?.();
      const axiosInstance = createAxiosInstance(token);
      
      try {
        if (isOnline && isServerReachable) {
          const response = await axiosInstance.delete(endpoint, config);
          
          // Remove from cache
          await deleteCachedItem(endpoint);
          
          return response;
        }
        throw new Error('Offline');
      } catch (error) {
        if (error.message === 'Offline' || error.code === 'ECONNABORTED' || !error.response) {
          isServerReachable = false;
          notifyConnectionChange();
          
          return await handleOfflineDelete(endpoint, getToken);
        }
        throw error;
      }
    },

    // Connection helpers
    isOnline: () => isOnline && isServerReachable,
    checkConnection: checkServerConnection,
    getConnectionState
  };

  return api;
};

// ==================== CACHE OPERATIONS ====================

/**
 * Cache API response
 */
const cacheResponse = async (endpoint, data) => {
  const store = findStoreForEndpoint(endpoint);
  if (!store) return;

  try {
    if (Array.isArray(data)) {
      // Mark items with cache metadata
      const itemsWithMeta = data.map(item => ({
        ...item,
        _cached_at: new Date().toISOString(),
        _is_synced: true
      }));
      await offlineDB.saveItems(store, itemsWithMeta);
    } else if (data && typeof data === 'object' && data.id) {
      await offlineDB.saveItem(store, {
        ...data,
        _cached_at: new Date().toISOString(),
        _is_synced: true
      });
    }
  } catch (error) {
    console.error('OfflineAPI: Failed to cache response:', error);
  }
};

/**
 * Get cached data for endpoint
 */
const getCachedData = async (endpoint) => {
  const store = findStoreForEndpoint(endpoint);
  if (!store) return null;

  try {
    // Check if endpoint has an ID (single item request)
    const idMatch = endpoint.match(/\/([^/]+)$/);
    const restaurantId = extractRestaurantId(endpoint);

    if (idMatch && !endpoint.endsWith('/menu') && !endpoint.endsWith('/orders') && 
        !endpoint.endsWith('/tables') && !endpoint.endsWith('/stock')) {
      // Single item request
      const item = await offlineDB.getItem(store, idMatch[1]);
      return item || null;
    }

    // List request - filter by restaurant if applicable
    const items = await offlineDB.getAllItems(store);
    
    if (restaurantId && items.length > 0 && items[0].restaurant_id) {
      return items.filter(item => item.restaurant_id === restaurantId);
    }
    
    return items;
  } catch (error) {
    console.error('OfflineAPI: Failed to get cached data:', error);
    return null;
  }
};

/**
 * Cache a newly created item
 */
const cacheCreatedItem = async (endpoint, data) => {
  const store = findStoreForEndpoint(endpoint);
  if (!store || !data || !data.id) return;

  try {
    await offlineDB.saveItem(store, {
      ...data,
      _cached_at: new Date().toISOString(),
      _is_synced: true
    });
  } catch (error) {
    console.error('OfflineAPI: Failed to cache created item:', error);
  }
};

/**
 * Update a cached item
 */
const updateCachedItem = async (endpoint, data) => {
  const store = findStoreForEndpoint(endpoint);
  if (!store) return;

  try {
    const idMatch = endpoint.match(/\/([^/]+)$/);
    const id = idMatch ? idMatch[1] : data.id;
    
    if (id) {
      const existing = await offlineDB.getItem(store, id);
      await offlineDB.saveItem(store, {
        ...existing,
        ...data,
        id,
        _cached_at: new Date().toISOString(),
        _is_synced: true
      });
    }
  } catch (error) {
    console.error('OfflineAPI: Failed to update cached item:', error);
  }
};

/**
 * Delete a cached item
 */
const deleteCachedItem = async (endpoint) => {
  const store = findStoreForEndpoint(endpoint);
  if (!store) return;

  try {
    const idMatch = endpoint.match(/\/([^/]+)$/);
    if (idMatch) {
      await offlineDB.deleteItem(store, idMatch[1]);
    }
  } catch (error) {
    console.error('OfflineAPI: Failed to delete cached item:', error);
  }
};

// ==================== OFFLINE OPERATIONS ====================

/**
 * Handle offline create operation
 */
const handleOfflineCreate = async (endpoint, data, getToken) => {
  const store = findStoreForEndpoint(endpoint);
  const localId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // Create local item
  const localItem = {
    ...data,
    id: localId,
    local_id: localId,
    _is_synced: false,
    _offline_created: true,
    _created_at: new Date().toISOString()
  };

  // Save to IndexedDB
  if (store) {
    await offlineDB.saveItem(store, localItem);
  }

  // Log audit action
  await logAuditAction('CREATE', getEntityType(endpoint), localId, { after: localItem });

  // Add to sync queue
  await addToSyncQueue({
    type: `CREATE_${getEntityType(endpoint).toUpperCase()}`,
    method: 'POST',
    endpoint,
    data,
    entity_type: getEntityType(endpoint),
    entity_id: localId,
    priority: getOperationPriority(endpoint, 'CREATE')
  });

  console.log(`OfflineAPI: Created locally and queued for sync: ${localId}`);

  return {
    data: { ...localItem, id: localId, local_id: localId },
    status: 201,
    offline: true
  };
};

/**
 * Handle offline update operation
 */
const handleOfflineUpdate = async (endpoint, data, getToken) => {
  const store = findStoreForEndpoint(endpoint);
  const idMatch = endpoint.match(/\/([^/]+)$/);
  const entityId = idMatch ? idMatch[1] : data.id;

  let existingItem = null;

  // Get existing item
  if (store && entityId) {
    existingItem = await offlineDB.getItem(store, entityId);
  }

  // Update local item
  const updatedItem = {
    ...existingItem,
    ...data,
    id: entityId,
    _is_synced: false,
    _offline_updated: true,
    _updated_at: new Date().toISOString()
  };

  // Save to IndexedDB
  if (store) {
    await offlineDB.saveItem(store, updatedItem);
  }

  // Log audit action
  await logAuditAction('UPDATE', getEntityType(endpoint), entityId, { 
    before: existingItem, 
    after: updatedItem 
  });

  // Add to sync queue
  await addToSyncQueue({
    type: `UPDATE_${getEntityType(endpoint).toUpperCase()}`,
    method: 'PUT',
    endpoint,
    data,
    entity_type: getEntityType(endpoint),
    entity_id: entityId,
    priority: getOperationPriority(endpoint, 'UPDATE')
  });

  console.log(`OfflineAPI: Updated locally and queued for sync: ${entityId}`);

  return {
    data: updatedItem,
    status: 200,
    offline: true
  };
};

/**
 * Handle offline delete operation
 */
const handleOfflineDelete = async (endpoint, getToken) => {
  const store = findStoreForEndpoint(endpoint);
  const idMatch = endpoint.match(/\/([^/]+)$/);
  const entityId = idMatch ? idMatch[1] : null;

  let existingItem = null;

  // Get existing item before delete
  if (store && entityId) {
    existingItem = await offlineDB.getItem(store, entityId);
    
    // Mark as deleted instead of actually deleting (for sync)
    if (existingItem) {
      existingItem._deleted = true;
      existingItem._deleted_at = new Date().toISOString();
      existingItem._is_synced = false;
      await offlineDB.saveItem(store, existingItem);
    }
  }

  // Log audit action
  await logAuditAction('DELETE', getEntityType(endpoint), entityId, { before: existingItem });

  // Add to sync queue
  await addToSyncQueue({
    type: `DELETE_${getEntityType(endpoint).toUpperCase()}`,
    method: 'DELETE',
    endpoint,
    data: null,
    entity_type: getEntityType(endpoint),
    entity_id: entityId,
    priority: getOperationPriority(endpoint, 'DELETE')
  });

  console.log(`OfflineAPI: Marked for deletion and queued for sync: ${entityId}`);

  return {
    data: { message: 'Queued for deletion' },
    status: 200,
    offline: true
  };
};

// ==================== HELPERS ====================

/**
 * Get entity type from endpoint
 */
const getEntityType = (endpoint) => {
  if (endpoint.includes('/orders')) return 'order';
  if (endpoint.includes('/menu')) return 'menu_item';
  if (endpoint.includes('/tables')) return 'table';
  if (endpoint.includes('/table-sessions')) return 'table_session';
  if (endpoint.includes('/table-bookings')) return 'booking';
  if (endpoint.includes('/stock')) return 'stock';
  if (endpoint.includes('/staff')) return 'staff';
  if (endpoint.includes('/waiters')) return 'waiter';
  if (endpoint.includes('/restaurants')) return 'restaurant';
  return 'unknown';
};

/**
 * Get operation priority (1=highest)
 */
const getOperationPriority = (endpoint, operation) => {
  // Orders are highest priority
  if (endpoint.includes('/orders')) return 1;
  
  // Table sessions and bookings
  if (endpoint.includes('/table-sessions') || endpoint.includes('/table-bookings')) return 1;
  
  // Stock updates
  if (endpoint.includes('/stock')) return 2;
  
  // Everything else
  return 3;
};

export default createOfflineAPI;
